import { Check, Copy } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { IconButton } from '../IconButton/IconButton.tsx';
import styles from './CopyButton.module.css';

export type CopyButtonProps = {
  text: string;
  label: string;
  copiedLabel: string;
  onCopyFailed: () => void;
};

const COPIED_FEEDBACK_MS = 1500;
const ICON_SIZE = 16;

const keepInside = (event: KeyboardEvent<HTMLButtonElement>) => {
  event.stopPropagation();
};

export const CopyButton = ({ text, label, copiedLabel, onCopyFailed }: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  const copy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      onCopyFailed();
    }
  };

  return (
    <>
      <IconButton
        size="sm"
        className={styles['button']}
        label={copied ? copiedLabel : label}
        icon={
          copied ? (
            <Check size={ICON_SIZE} aria-hidden="true" />
          ) : (
            <Copy size={ICON_SIZE} aria-hidden="true" />
          )
        }
        onClick={(event) => {
          void copy(event);
        }}
        onKeyDown={keepInside}
      />
      <span role="status" className={styles['announcement']}>
        {copied ? copiedLabel : ''}
      </span>
    </>
  );
};
